"use client";

import { useState } from "react";
import { ChevronRight, MessageCircle, Phone, TrendingUp, CheckCircle2 } from "lucide-react";
import {
  BROKING_STAGES,
  STAGE_ORDER,
  GUIDED_STAGES,
  segmentMeta,
  rmById,
  formatINRCompact,
  type BrokingLead,
} from "@/lib/demos/stockBrokingData";

interface StockBrokingKanbanProps {
  leads: BrokingLead[];
  onSelectLead: (lead: BrokingLead) => void;
  onRequestStageAdvance: (lead: BrokingLead, targetStage: string) => void;
  onDirectStageMove: (leadId: number, targetStage: string) => void;
  onOpenChat: (lead: BrokingLead) => void;
}

const PRIORITY_STYLE: Record<BrokingLead["priority"], string> = {
  HOT: "bg-rose-500/15 text-rose-700 border-rose-500/25",
  WARM: "bg-amber-500/15 text-amber-700 border-amber-500/25",
  COLD: "bg-blue-500/15 text-blue-700 border-blue-500/25",
};

export function StockBrokingKanban({
  leads,
  onSelectLead,
  onRequestStageAdvance,
  onDirectStageMove,
  onOpenChat,
}: StockBrokingKanbanProps) {
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);

  function handleDrop(e: React.DragEvent, targetStage: string) {
    e.preventDefault();
    setOverStage(null);
    const id = Number(e.dataTransfer.getData("text/plain"));
    setDraggedId(null);
    if (!id) return;
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.stage === targetStage) return;
    if (GUIDED_STAGES.includes(targetStage as BrokingLead["stage"])) {
      onRequestStageAdvance(lead, targetStage);
    } else {
      onDirectStageMove(id, targetStage);
    }
  }

  function nextStage(current: string): string | null {
    const idx = STAGE_ORDER.indexOf(current as BrokingLead["stage"]);
    // Don't auto-advance into the terminal "Dormant / Recall" column.
    if (idx >= 0 && idx < STAGE_ORDER.length - 2) return STAGE_ORDER[idx + 1];
    return null;
  }

  return (
    <div className="flex gap-3.5 overflow-x-auto pb-6 snap-x px-0.5">
      {BROKING_STAGES.map((stage) => {
        const stageLeads = leads.filter((l) => l.stage === stage.name);
        const isOver = overStage === stage.name;
        return (
          <div
            key={stage.id}
            onDragOver={(e) => {
              e.preventDefault();
              setOverStage(stage.name);
            }}
            onDragLeave={() => setOverStage((s) => (s === stage.name ? null : s))}
            onDrop={(e) => handleDrop(e, stage.name)}
            className={`flex flex-col rounded-2xl border p-3 w-[270px] shrink-0 snap-start transition-colors ${
              isOver ? "bg-emerald-500/5 border-emerald-500/40" : "bg-white border-slate-200"
            }`}
          >
            {/* Column header */}
            <div className="flex items-center justify-between pb-2.5 mb-2.5 border-b border-slate-200">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`w-2 h-2 rounded-full shrink-0 ${stage.dot}`} />
                <h4 className="text-[11px] font-bold text-slate-700 leading-tight line-clamp-2">{stage.name}</h4>
              </div>
              <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0">
                {stageLeads.length}
              </span>
            </div>

            {/* Cards */}
            <div className="space-y-2.5 flex-1 overflow-y-auto max-h-[calc(100vh-300px)] pr-0.5">
              {stageLeads.length === 0 ? (
                <div className="py-6 text-center border-2 border-dashed border-slate-200 rounded-xl">
                  <p className="text-[10px] text-slate-500 font-medium">Drop client here</p>
                </div>
              ) : (
                stageLeads.map((lead) => {
                  const rm = rmById(lead.rm_id);
                  const next = nextStage(lead.stage);
                  const checklist = [lead.aadhaar_linked, lead.ipv_done, lead.bank_verified, lead.esign_done];
                  const done = checklist.filter(Boolean).length;
                  return (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", String(lead.id));
                        setDraggedId(lead.id);
                      }}
                      onDragEnd={() => setDraggedId(null)}
                      onClick={() => onSelectLead(lead)}
                      className={`p-3 rounded-xl bg-white border border-slate-300 hover:border-emerald-500/40 cursor-pointer transition-all hover:shadow-lg hover:shadow-emerald-950/20 group ${
                        draggedId === lead.id ? "opacity-40" : ""
                      }`}
                    >
                      {/* Top row: client code + priority */}
                      <div className="flex items-center justify-between gap-1 mb-1.5">
                        <span className="text-[10px] font-mono text-emerald-600 font-semibold bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
                          {lead.client_code}
                        </span>
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${PRIORITY_STYLE[lead.priority]}`}>
                          {lead.priority}
                        </span>
                      </div>

                      {/* Name + city */}
                      <h5 className="text-xs font-bold text-slate-900 group-hover:text-emerald-700 transition-colors line-clamp-1">
                        {lead.name}
                      </h5>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {lead.city} · {rm?.name.split(" ")[0] ?? "Unassigned"}
                      </p>

                      {/* Segment badges */}
                      {lead.segments_active.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {lead.segments_active.slice(0, 3).map((sid) => {
                            const seg = segmentMeta(sid);
                            return seg ? (
                              <span key={sid} className={`text-[8px] font-bold px-1.5 py-0.5 rounded border ${seg.color}`}>
                                {seg.short}
                              </span>
                            ) : null;
                          })}
                          {lead.segments_active.length > 3 && (
                            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                              +{lead.segments_active.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      {/* KYC checklist mini-progress (only pre-activation) */}
                      {["New Enquiry", "KYC (PAN / Aadhaar / IPV)", "Bank & Nominee", "E-Sign"].includes(lead.stage) && (
                        <div className="mt-2 flex items-center gap-1.5">
                          <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-amber-400 to-emerald-500" style={{ width: `${(done / 4) * 100}%` }} />
                          </div>
                          <span className="text-[9px] text-slate-500 font-mono">{done}/4</span>
                        </div>
                      )}

                      {/* AUM / brokerage (activated) */}
                      {lead.aum > 0 && (
                        <div className="flex items-center justify-between gap-2 text-[10px] mt-2 pt-2 border-t border-slate-200 whitespace-nowrap">
                          <span className="text-slate-500 truncate">AUM <span className="text-slate-900 font-bold">{formatINRCompact(lead.aum)}</span></span>
                          {lead.brokerage_mtd > 0 && (
                            <span className="text-emerald-600 font-semibold flex items-center gap-0.5 shrink-0">
                              <TrendingUp className="w-3 h-3" />{formatINRCompact(lead.brokerage_mtd)}
                            </span>
                          )}
                        </div>
                      )}

                      {/* SIP flag */}
                      {lead.sip_active && (
                        <div className="mt-1.5 text-[9px] text-teal-700 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> SIP {formatINRCompact(lead.sip_amount)}/mo
                        </div>
                      )}

                      {/* Actions */}
                      <div className="mt-2.5 pt-2 border-t border-slate-200 flex items-center justify-between gap-1" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <a
                            href={`tel:${lead.phone.replace(/\s/g, "")}`}
                            className="p-1.5 rounded-lg bg-slate-100 hover:bg-emerald-600/20 text-emerald-600 transition-colors"
                            title="Click to call"
                          >
                            <Phone className="w-3 h-3" />
                          </a>
                          <button
                            type="button"
                            onClick={() => onOpenChat(lead)}
                            className="p-1.5 rounded-lg bg-slate-100 hover:bg-emerald-600/20 text-emerald-600 transition-colors"
                            title="Open WhatsApp chat"
                          >
                            <MessageCircle className="w-3 h-3" />
                          </button>
                        </div>
                        {next && (
                          <button
                            type="button"
                            onClick={() => {
                              if (GUIDED_STAGES.includes(next as BrokingLead["stage"])) {
                                onRequestStageAdvance(lead, next);
                              } else {
                                onDirectStageMove(lead.id, next);
                              }
                            }}
                            className="px-2 py-1 rounded-lg bg-emerald-500/10 hover:bg-emerald-500 text-emerald-700 hover:text-white text-[9px] font-bold border border-emerald-500/20 hover:border-transparent transition-all flex items-center gap-0.5"
                          >
                            Advance <ChevronRight className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
